import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom"
import { UndoProvider } from "./context/UndoContext"
import { Box, CircularProgress } from "@mui/material"

import { AuthProvider, useAuth } from "./context/AuthContext"
import { SocketProvider } from "./context/SocketContext"
import { NotificationProvider } from "./context/NotificationContext"
import { TimerProvider } from "./context/TimerContext"
import { AppThemeProvider } from "./context/ThemeContext"
import { usePermissions } from "./permissions"

import AppShell from "./shell/AppShell"
import Login from "./auth/Login"
import ForgotPassword from "./auth/ForgotPassword"
import SetPassword from "./auth/SetPassword"
import DashboardPage from "./pages/DashboardPage"
import ChatPage from "./pages/ChatPage"
import TasksPage from "./pages/TasksPage"
import ProjectsPage from "./pages/ProjectsPage"
import ProjectDetailPage from "./pages/ProjectDetailPage"
import ClientsPage from "./pages/ClientsPage"
import DrivePage from "./pages/DrivePage"
import UserDetailPage from "./pages/UserDetailPage"
import VaultPage from "./pages/VaultPage"
import UsersPage from "./pages/UsersPage"
import RolesPage from "./pages/RolesPage"
import SettingsPage from "./pages/SettingsPage"
import TimesheetPage from "./pages/TimesheetPage"
import TimeReviewPage from "./pages/TimeReviewPage"
import TimeReportPage from "./pages/TimeReportPage"
import BillingPage from "./pages/BillingPage"
import InvoicesPage from "./pages/InvoicesPage"
import NotFoundPage from "./pages/NotFoundPage"

/**
 * Gate a route on a permission.
 *
 * Redirects rather than rendering an error: the sidebar already hides what a user
 * cannot reach, so arriving here means a stale bookmark or a link from somebody
 * with different access — and for that, landing somewhere useful beats a
 * dead end.
 */
function RequirePermission({ module, action, children }) {
  const { can } = usePermissions()
  return can(module, action) ? children : <Navigate to="/" replace />
}

/*
 * Paths that belong to nobody in particular.
 *
 * These are reached from a link in an email, so they have to render whatever the
 * session state is. Bouncing a signed-in visitor to the dashboard — the obvious
 * thing for a router to do with an auth screen — breaks the one case that
 * matters: somebody who still has a live session on this machine and clicked a
 * reset link precisely because they want the password changed.
 */
const PUBLIC_AUTH_PATHS = ["/forgot-password", "/set-password", "/reset-password"]

function PublicAuthRoutes() {
  return (
    <Routes>
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/* One component. The backend completes an invite and a reset through the
          same endpoint; only the wording differs, and that is what `mode` picks. */}
      <Route path="/set-password" element={<SetPassword mode="invite" />} />
      <Route path="/reset-password" element={<SetPassword mode="reset" />} />
    </Routes>
  )
}

function AuthenticatedApp() {
  const { isAuthenticated, loading } = useAuth()
  const { pathname } = useLocation()
  const isPublicAuthPath = PUBLIC_AUTH_PATHS.includes(pathname)

  // A token in storage is restored by calling /auth/me. Rendering the login
  // screen during that check flashes it for every returning user.
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isPublicAuthPath) return <PublicAuthRoutes />

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Any other path falls through to the login screen rather than
            redirecting, so the address the person typed survives the sign-in and
            is still there to return to. */}
        <Route path="*" element={<Login />} />
      </Routes>
    )
  }

  return (
    <SocketProvider>
      <NotificationProvider>
        <TimerProvider>
          <UndoProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />

            <Route
              path="chat"
              element={
                <RequirePermission module="chat" action="view">
                  <ChatPage />
                </RequirePermission>
              }
            />
            {/* The same component: opening a thread is a URL, so it can be
                bookmarked, linked from a notification, and reloaded. */}
            <Route
              path="chat/:roomId"
              element={
                <RequirePermission module="chat" action="view">
                  <ChatPage />
                </RequirePermission>
              }
            />

            <Route
              path="tasks"
              element={
                <RequirePermission module="tasks" action="view">
                  <TasksPage />
                </RequirePermission>
              }
            />
            <Route
              path="tasks/:taskId"
              element={
                <RequirePermission module="tasks" action="view">
                  <TasksPage />
                </RequirePermission>
              }
            />

            <Route
              path="projects"
              element={
                <RequirePermission module="projects" action="view">
                  <ProjectsPage />
                </RequirePermission>
              }
            />
            <Route
              path="projects/:projectId"
              element={
                <RequirePermission module="projects" action="view">
                  <ProjectDetailPage />
                </RequirePermission>
              }
            />

            {/* The document store and the vault. Both check again per item:
                holding the module means the store exists for you, not that
                every folder or entry in it does. */}
            <Route
              path="files"
              element={
                <RequirePermission module="drive" action="view">
                  <DrivePage />
                </RequirePermission>
              }
            />
            <Route
              path="vault"
              element={
                <RequirePermission module="vault" action="view">
                  <VaultPage />
                </RequirePermission>
              }
            />

            <Route
              path="clients"
              element={
                <RequirePermission module="clients" action="view">
                  <ClientsPage />
                </RequirePermission>
              }
            />
            <Route
              path="users"
              element={
                <RequirePermission module="users" action="view">
                  <UsersPage />
                </RequirePermission>
              }
            />
            {/* The person, at a URL. A report row, a task's assignee and the
                users list all point at the same page rather than three
                different partial views of the same human being. */}
            <Route
              path="users/:userId"
              element={
                <RequirePermission module="users" action="view">
                  <UserDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="roles"
              element={
                <RequirePermission module="rbac_admin" action="view">
                  <RolesPage />
                </RequirePermission>
              }
            />

            <Route
              path="timesheet"
              element={
                <RequirePermission module="time_tracking" action="log">
                  <TimesheetPage />
                </RequirePermission>
              }
            />
            <Route
              path="time-reports"
              element={
                <RequirePermission module="time_reports" action="view">
                  <TimeReportPage />
                </RequirePermission>
              }
            />
            <Route
              path="time-review"
              element={
                <RequirePermission module="time_tracking" action="view_all">
                  <TimeReviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="billing"
              element={
                <RequirePermission module="billing" action="invoice">
                  <BillingPage />
                </RequirePermission>
              }
            />
            <Route
              path="invoices"
              element={
                <RequirePermission module="billing" action="view">
                  <InvoicesPage />
                </RequirePermission>
              }
            />

            <Route path="settings" element={<SettingsPage />} />
            {/* Already signed in and back on the login URL — a bookmark, or the
                browser restoring a tab. Nothing to sign into. */}
            <Route path="login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
          </UndoProvider>
        </TimerProvider>
      </NotificationProvider>
    </SocketProvider>
  )
}

export default function App() {
  return (
    <AppThemeProvider>
      {/* BrowserRouter wraps AuthProvider because NotificationProvider uses
          useNavigate to route a notification click, and a hook cannot reach a
          router that is mounted below it. */}
      <BrowserRouter>
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </BrowserRouter>
    </AppThemeProvider>
  )
}
